import { Notification, NotificationSettings } from '../types';
import { requestNotificationPermission, onForegroundMessage } from './firebase';

class NotificationService {
  private static instance: NotificationService;
  private notifications: Notification[] = [];
  private settings: NotificationSettings | null = null;
  private listeners: Set<(notifications: Notification[]) => void> = new Set();
  private foregroundUnsubscribe: (() => void) | null = null;

  private constructor() {
    this.initializeService();
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  private async initializeService() {
    // Request notification permission
    await requestNotificationPermission();

    // Set up foreground message listener
    this.foregroundUnsubscribe = onForegroundMessage((payload) => {
      // Handle foreground message
      this.handleForegroundMessage(payload);
    });
  }

  private handleForegroundMessage(payload: any) {
    // Handle incoming notification when app is in foreground
    const notification: Notification = {
      id: payload.messageId || Date.now().toString(),
      title: payload.notification?.title || 'New Notification',
      message: payload.notification?.body || '',
      type: payload.data?.type || 'general',
      read: false,
      createdAt: payload.data?.createdAt || payload.sentTime || new Date().toISOString(),
      userId: payload.data?.userId || '',
      metadata: payload.data?.metadata ? JSON.parse(payload.data.metadata) : undefined
    };

    this.addNotification(notification);
  }

  // Subscribe to notification updates
  public subscribe(listener: (notifications: Notification[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Notify all listeners
  private notifyListeners() {
    this.listeners.forEach(listener => listener([...this.notifications]));
  }

  // Add a new notification
  public addNotification(notification: Notification) {
    this.notifications.unshift(notification);
    this.notifyListeners();
  }

  // Mark notification as read
  public markAsRead(notificationId: string) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.notifyListeners();
    }
  }

  // Mark all notifications as read
  public markAllAsRead() {
    this.notifications.forEach(notification => {
      notification.read = true;
    });
    this.notifyListeners();
  }

  // Get all notifications
  public getNotifications(): Notification[] {
    return [...this.notifications];
  }

  // Get unread count
  public getUnreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  // Get notification settings
  public getSettings(): NotificationSettings | null {
    return this.settings;
  }

  // Update notification settings
  public updateSettings(settings: NotificationSettings) {
    this.settings = settings;
  }

  // Create notification for SOP added
  public createSopAddedNotification(sopTitle: string, sopId: string, userId: string, createdAt?: string) {
    const notification: Notification = {
      id: Date.now().toString(),
      title: 'New SOP Available',
      message: `A new SOP "${sopTitle}" has been added and is now available for you to query.`,
      type: 'sop_added',
      read: false,
      createdAt: createdAt || new Date().toISOString(),
      userId,
      metadata: {
        sopId,
        sopTitle
      }
    };
    this.addNotification(notification);
  }

  // Create notification for SOP access changed
  public createSopAccessChangedNotification(sopTitle: string, sopId: string, userId: string, createdAt?: string) {
    const notification: Notification = {
      id: Date.now().toString(),
      title: 'SOP Access Updated',
      message: `Your access to SOP "${sopTitle}" has been updated.`,
      type: 'sop_access_changed',
      read: false,
      createdAt: createdAt || new Date().toISOString(),
      userId,
      metadata: {
        sopId,
        sopTitle
      }
    };
    this.addNotification(notification);
  }

  // Create notification for user onboarded (for admins)
  public createUserOnboardedNotification(
    managerName: string, 
    onboardedUserName: string, 
    onboardedUserEmail: string, 
    onboardedUserRole: string,
    userId: string,
    createdAt?: string
  ) {
    const notification: Notification = {
      id: Date.now().toString(),
      title: 'New User Onboarded',
      message: `${managerName} has onboarded a new user: ${onboardedUserName} (${onboardedUserEmail}) assigned as ${onboardedUserRole.charAt(0).toUpperCase() + onboardedUserRole.slice(1)}`,
      type: 'user_onboarded',
      read: false,
      createdAt: createdAt || new Date().toISOString(),
      userId,
      metadata: {
        managerName,
        onboardedUserName,
        onboardedUserEmail,
        onboardedUserRole
      }
    };
    this.addNotification(notification);
  }

  // Cleanup
  public destroy() {
    if (this.foregroundUnsubscribe) {
      this.foregroundUnsubscribe();
    }
    this.listeners.clear();
  }
}

export default NotificationService;
